import {
  joinSupplierPartRows,
  type JoinedSupplierPartRow,
} from "./joinSupplierPartRows";
import { PartQuantity, SupplierPart } from "./partsCalculationUtils";

export type PartConfirmationLookup = {
  quantity: number;
  status?: "confirmed" | "declined";
};

export type AddToSupplierBucketOptions = {
  /** View Details already applied user-edited qty; do not recap. */
  skipAssignQtyCap?: boolean;
  /**
   * Full supplier hit list for confirmation checks when `supplierParts` is only
   * the selected subset (View Details → Add to part bucket).
   */
  supplierPartsForConfirmationCheck?: Array<SupplierPart & { id?: string }>;
};

export const confirmationForRow = (
  row: JoinedSupplierPartRow,
  confirmedByPartId: Map<string, PartConfirmationLookup>,
): PartConfirmationLookup | undefined =>
  confirmedByPartId.get(row.partId) || confirmedByPartId.get(row.partNumber);

/** True when every stocked order part has status confirmed or declined. */
export const hasSupplierRespondedToAllParts = (
  remainingParts: PartQuantity[],
  supplierParts: Array<SupplierPart & { id?: string }>,
  confirmedByPartId: Map<string, PartConfirmationLookup>,
): boolean => {
  const rows = joinSupplierPartRows(remainingParts, supplierParts);
  if (rows.length === 0) {
    return true;
  }
  return rows.every((row) => {
    const status = confirmationForRow(row, confirmedByPartId)?.status;
    return status === "confirmed" || status === "declined";
  });
};

export const resolvedConfirmedQty = (
  confirmation?: PartConfirmationLookup,
): number | undefined => {
  if (!confirmation) {
    return undefined;
  }
  if (confirmation.status === "declined") {
    return 0;
  }
  if (confirmation.status === "confirmed") {
    return confirmation.quantity ?? 0;
  }
  return undefined;
};

/** Same default/max as View Details: min(confirmed ?? stock, asked). Declined → 0. */
export const assignQtyMaxForRow = (
  row: JoinedSupplierPartRow,
  confirmation?: PartConfirmationLookup,
): number => {
  const askedQty = Math.max(0, Number(row.askedQty) || 0);
  const stockQty = Math.max(0, Number(row.stockQty) || 0);
  const cap = resolvedConfirmedQty(confirmation) ?? stockQty;
  return Math.max(0, Math.min(cap, askedQty));
};

export const buildConfirmedByPartId = (
  parts: Array<{
    partId?: string;
    partNumber?: string;
    quantity?: number;
    confirmedQty?: number | null;
    status?: "confirmed" | "declined" | string;
    responseStatus?: "confirmed" | "declined" | string;
  }> = [],
): Map<string, PartConfirmationLookup> => {
  const map = new Map<string, PartConfirmationLookup>();
  parts.forEach((part) => {
    const rawStatus = String(part.responseStatus ?? part.status ?? "")
      .trim()
      .toLowerCase();
    const status: PartConfirmationLookup["status"] =
      rawStatus === "declined"
        ? "declined"
        : rawStatus === "confirmed"
          ? "confirmed"
          : undefined;
    const value = {
      quantity:
        part.confirmedQty !== undefined && part.confirmedQty !== null
          ? Number(part.confirmedQty) || 0
          : part.quantity ?? 0,
      status,
    };
    if (part.partId) {
      map.set(part.partId, value);
    }
    if (part.partNumber) {
      map.set(part.partNumber, value);
    }
  });
  return map;
};

/**
 * Parts View Details would add on open + "Add to part bucket" with defaults:
 * selected when assign qty > 0, quantity = that default.
 */
export const buildDefaultSelectedPartsForBucket = (
  remainingParts: PartQuantity[],
  supplierHits: Array<SupplierPart & { id?: string }>,
  confirmedByPartId: Map<string, PartConfirmationLookup>,
): Array<
  SupplierPart & { id?: string; rootPartNumber: string; familyQuantity: number }
> =>
  joinSupplierPartRows(remainingParts, supplierHits).flatMap((row) => {
    const quantity = assignQtyMaxForRow(
      row,
      confirmationForRow(row, confirmedByPartId),
    );
    if (quantity <= 0) {
      return [];
    }
    return [
      {
        ...row.supplierPart,
        rootPartNumber: row.rootPartNumber,
        familyQuantity: quantity,
      },
    ];
  });

/**
 * Whether any remaining part is still selectable for this supplier under the
 * same rules as View Details (declined → 0, confirmed → confirmed qty, else asked).
 * Parts with no PC response are treated as still selectable.
 */
export const supplierHasSelectableRemainingQty = (
  remainingParts: PartQuantity[],
  confirmedByPartId: Map<string, PartConfirmationLookup>,
): boolean => {
  if (remainingParts.length === 0) {
    return false;
  }

  return remainingParts.some((part) => {
    const confirmation =
      (part.partId
        ? confirmedByPartId.get(String(part.partId))
        : undefined) ||
      (part.partNumber
        ? confirmedByPartId.get(String(part.partNumber))
        : undefined);

    if (!confirmation) {
      return true;
    }

    const askedQty = Math.max(0, Number(part.quantity) || 0);
    const stockQty =
      confirmation.status === "declined"
        ? 0
        : confirmation.status === "confirmed"
          ? Math.max(0, Number(confirmation.quantity) || 0)
          : askedQty;

    return (
      assignQtyMaxForRow(
        {
          partId: String(part.partId ?? ""),
          partNumber: part.partNumber,
          rootPartNumber: part.rootPartNumber,
          partName: part.partName,
          askedQty,
          stockQty,
          reportedStockQty: stockQty,
          binLocation: "",
          parts: [],
          alternateParts: [],
          supplierPart: {
            rootPartNumber: part.rootPartNumber,
            familyQuantity: stockQty,
          },
        },
        confirmation,
      ) > 0
    );
  });
};

/**
 * Suppliers with PC responses where no remaining row is selectable (e.g. all
 * declined) — hide from discovery so they do not mislead.
 */
export const buildDiscoveryExcludedSupplierIdsFromPartConfirmations = (
  responsesBySupplierId: Map<
    string,
    Array<{
      partId?: string;
      partNumber?: string;
      quantity?: number;
      confirmedQty?: number | null;
      status?: string;
      responseStatus?: string;
    }>
  >,
  remainingParts: PartQuantity[],
): string[] => {
  if (remainingParts.length === 0 || responsesBySupplierId.size === 0) {
    return [];
  }

  const excluded: string[] = [];
  responsesBySupplierId.forEach((parts, supplierId) => {
    if (!supplierId || !(parts ?? []).length) return;
    const confirmedByPartId = buildConfirmedByPartId(parts);
    if (!supplierHasSelectableRemainingQty(remainingParts, confirmedByPartId)) {
      excluded.push(String(supplierId));
    }
  });
  return excluded;
};